import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import { formatCurrencyAmount } from "../../../../../helpers/formatAmount";
import "./styles/tax-projection.css";
export const TaxProjectionOverview = ({ data }) => {
  if (!data) {
    return <div className="tax-no-data">No data to show</div>;
  }

  // State not supported → show friendly error card
  if (data.error === true || data.stateNotSupported) {
    const stateName = data.stateNotSupported || "This state";

    return (
      <div className="tax-error-card">
        {/* Optional icon */}
        <div className="tax-error-icon">Warning</div>
        <h2 className="tax-error-title">
          {stateName} Tax Calculations Not Yet Available
        </h2>
        <p className="tax-error-message">
          {data.message ||
            `We're actively working on adding full tax support for ${stateName}. 
            It will be ready very soon thank you for your patience!`}
        </p>
        <div className="tax-error-badge">
          Support for more states arriving soon
        </div>{" "}
      </div>
    );
  }

  const [activeTab, setActiveTab] = useState("tableView");

  const projections = data && data.projections ? data.projections : [];
  const chartData = data && data.chartData ? data.chartData : {};
  const capitalGains = data && data.capitalGains ? data.capitalGains : {};

  // ✅ Prepare Chart Dataset
  const lineChartData = {
    labels: chartData.labels || [],
    datasets: [
      {
        label: "Federal Tax",
        data: chartData.datasets ? chartData.datasets.federal : [],
        borderColor: "#C9D5F1",
        backgroundColor: "#C9D5F1",
        tension: 0.3,
        fill: true,
        order: 3,
      },
      {
        label: "State Tax",
        data: chartData.datasets ? chartData.datasets.state : [],
        borderColor: "#E53E3E",
        backgroundColor: "#E53E3E",
        tension: 0.3,
        fill: true,
        order: 2,
      },
      {
        label: "Total Tax",
        data: chartData.datasets ? chartData.datasets.total : [],
        borderColor: "#2A3DA3",
        backgroundColor: "#2A3DA3",
        tension: 0.3,
        fill: true,
        order: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      tooltip: { mode: "index", intersect: false },
    },
    interaction: { mode: "nearest", intersect: false },
    scales: {
      yAxes: [
        {
          gridLines: {
            color: "rgba(0, 0, 0, 0)", // Make grid lines invisible
          },
          title: { display: true, text: "Tax ($)" },
          ticks: { callback: (v) => `$${v.toLocaleString()}` },
        },
      ],
      xAxes: [
        {
          gridLines: {
            color: "rgba(0, 0, 0, 0)", // Make grid lines invisible
          },
          title: { display: true, text: "Year" },
        },
      ],
    },
  };

  return (
    <div
      className="social-security-main"
      style={{ width: "100%", padding: "1rem 3rem" }}
    >
      <div className="calc-breakdown-container">
        {/* Tax Rate Cards (already perfect) */}
        <div className="tax-rate-cards">
          <div className="tax-rate-card">
            <div className="tax-rate-label">Effective Tax Rate</div>
            <div className="tax-rate-value tax-rate-effective">
              {data.effectiveRate
                ? parseFloat(data.effectiveRate).toFixed(2)
                : "0.00"}
              %
            </div>
            <div className="tax-rate-note">
              Total tax as a percentage of gross income
            </div>
          </div>
          <div className="tax-rate-card">
            <div className="tax-rate-label">Marginal Tax Rate</div>
            <div className="tax-rate-value tax-rate-marginal">
              {data.marginalRate
                ? parseFloat(data.marginalRate).toFixed(2)
                : "0.00"}
              %
            </div>
            <div className="tax-rate-note">
              Tax on your next dollar of income
            </div>
          </div>
        </div>

        {/* Calculation Breakdown */}
        <h2 className="calc-breakdown-title">Calculation Breakdown</h2>

        <div>
          <div className="calc-row">
            <span className="calc-label">Annual Gross Income</span>
            <span className="calc-value">
              {data.grossIncome && formatCurrencyAmount(data.grossIncome)}
            </span>
          </div>

          <div className="calc-row highlight">
            <span className="calc-label">Capital Loss Deduction</span>
            <span className="calc-value negative">
              {data.capLossDeduction !== undefined &&
                (data.capLossDeduction > 0
                  ? `-${formatCurrencyAmount(data.capLossDeduction)}`
                  : "$0.00")}
            </span>
          </div>

          <div className="calc-divider"></div>

          <div className="calc-row">
            <span className="calc-label">Adjusted Gross Income (AGI)</span>
            <span className="calc-value">
              {data.AGI && formatCurrencyAmount(data.AGI)}
            </span>
          </div>
          <div className="calc-row">
            <span className="calc-label">Deduction Type</span>
            <span className="calc-value">
              {data.deductionType &&
                `${data.deductionType} (${formatCurrencyAmount(data.federalDeduction || 0)})`}
            </span>
          </div>
          <div className="calc-row">
            <span className="calc-label">Ordinary Taxable Income</span>
            <span className="calc-value">
              {data.ordTaxable && formatCurrencyAmount(data.ordTaxable)}
            </span>
          </div>
          <div className="calc-row">
            <span className="calc-label">Net Long-Term Capital Gains</span>
            <span className="calc-value">
              {data.ltcgTaxable !== undefined
                ? formatCurrencyAmount(data.ltcgTaxable)
                : "$0.00"}
            </span>
          </div>

          <div className="calc-divider"></div>

          <div className="calc-section-title">Federal Tax Before Credits</div>
          <div className="calc-row">
            <span className="calc-label">
              Federal Ordinary Tax (Pre-Credit)
            </span>
            <span className="calc-value">
              {data.fedOrdTax && formatCurrencyAmount(data.fedOrdTax)}
            </span>
          </div>
          <div className="calc-row">
            <span className="calc-label">Federal LTCG Tax</span>
            <span className="calc-value">
              {data.fedLtcgTax
                ? formatCurrencyAmount(data.fedLtcgTax)
                : "$0.00"}
            </span>
          </div>

          <div className="calc-row highlight">
            <span className="calc-label">(−) Non-Refundable Credits</span>
            <span className="calc-value green">
              {data.nonRefundableCredits > 0
                ? `-${formatCurrencyAmount(data.nonRefundableCredits)}`
                : "$0.00"}
            </span>
          </div>

          <div className="calc-row highlight">
            <span className="calc-label">(−) Child Tax Credit</span>
            <span className="calc-value green">
              {data.ctc > 0 ? `-${formatCurrencyAmount(data.ctc)}` : "$0.00"}
            </span>
          </div>

          <div className="calc-row">
            <span className="calc-label">No. of Dependents</span>
            <span className="calc-value">
              {" "}
              {data.numDependents ? data.numDependents : "0"}
            </span>
          </div>

          <div className="calc-row highlight">
            <span className="calc-label">(−) Refundable Credits</span>
            <span className="calc-value green">
              {data.refundableCredits > 0
                ? `-${formatCurrencyAmount(data.refundableCredits)}`
                : "$0.00"}
            </span>
          </div>

          <div className="calc-row total">
            <span className="calc-label">Total Federal Tax</span>
            <span className="calc-value">
              {data.fedTax && formatCurrencyAmount(data.fedTax)}
            </span>
          </div>

          <div className="calc-divider"></div>

          <div className="calc-section-title">State & Local + Payroll</div>
          <div className="calc-row">
            <span className="calc-label">
              State Taxable Income ({data.stateName})
            </span>
            <span className="calc-value">
              {data.stateTaxable ? formatCurrencyAmount(data.stateTaxable) : "$0.00"}
            </span>
          </div>
          <div className="calc-row">
            <span className="calc-label">State Tax</span>
            <span className="calc-value">
              {data.stateTax ? formatCurrencyAmount(data.stateTax) : "$0.00"}
            </span>
          </div>
          <div className="calc-row">
            <span className="calc-label">Local Tax ({data.cityName})</span>
            <span className="calc-value">
              {data.localTax !== undefined
                ? formatCurrencyAmount(data.localTax)
                : "$0.00"}
            </span>
          </div>
          <div className="calc-row">
            <span className="calc-label">Payroll Tax (FICA/SE)</span>
            <span className="calc-value">
              {data.payrollTax && formatCurrencyAmount(data.payrollTax)}
            </span>
          </div>

          <div
            className="calc-row total"
            style={{ marginTop: "24px", fontSize: "20px" }}
          >
            <span className="calc-label">Total Tax Liability</span>
            <span
              className="calc-value"
              style={{ fontSize: "24px", color: "#2a3da3" }}
            >
              {data.totalTaxLiability &&
                formatCurrencyAmount(data.totalTaxLiability)}
            </span>
          </div>
        </div>
      </div>
      <h2 className="text-xl font-semibold text-gray-800 mb-4 mt-4">
        Tax Projection Overview
      </h2>
      <div className="social-security-toggle-container mb-4">
        <div
          className={`social-security-toggle-button-container ${activeTab === "tableView" ? "active" : ""
            }`}
        >
          <button
            className={`social-security-toggle-button ${activeTab === "tableView" ? "active" : ""
              }`}
            onClick={() => setActiveTab("tableView")}
          >
            Detailed Table
          </button>
        </div>
        <div
          className={`social-security-toggle-button-container ${activeTab === "graphView" ? "active" : ""
            }`}
        >
          <button
            className={`social-security-toggle-button ${activeTab === "graphView" ? "active" : ""
              }`}
            onClick={() => setActiveTab("graphView")}
          >
            Tax Trend Graph
          </button>
        </div>
      </div>

      {/* Graph View */}
      {activeTab === "graphView" && (
        <div className="bg-white rounded-2xl p-4">
          <Line height={90} data={lineChartData} options={options} />
        </div>
      )}

      {/* Table View */}
      {activeTab === "tableView" && (
        <div
          style={{
            overflowY: "auto",
            border: "1px solid #ddd",
            borderRadius: "8px",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
            }}
          >
            <thead>
              <tr
                style={{
                  background: "rgb(244, 247, 254)",
                  position: "sticky",
                  top: 0,
                }}
              >
                <th style={thStyle}>Year</th>
                <th style={thStyle}>Income ($)</th>
                <th style={thStyle}>Federal Tax ($)</th>
                <th style={thStyle}>State Tax ($)</th>
                <th style={thStyle}>Refundable Credits ($)</th>
                <th style={thStyle}>Non-Refundable Credits ($)</th>
                <th style={thStyle}>Initial Tax ($)</th>
                <th style={thStyle}>Total Tax ($)</th>
                <th style={thStyle}>Tax Liability ($)</th>
                <th style={thStyle}>Effective Rate (%)</th>
                <th style={thStyle}>Marginal Rate (%)</th>
              </tr>
            </thead>
            <tbody>
              {projections &&
                projections.map(function (p, i) {
                  return (
                    <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                      <td style={tdStyle}>{p.year}</td>
                      <td style={tdStyle}>{formatCurrencyAmount(p.income)}</td>
                      <td style={tdStyle}>
                        {formatCurrencyAmount(
                          p.federal && p.federal.tax ? p.federal.tax : 0
                        )}
                      </td>
                      <td style={tdStyle}>
                        {formatCurrencyAmount(
                          p.state && p.state.tax ? p.state.tax : 0
                        )}
                      </td>
                      <td style={tdStyle}>
                        {formatCurrencyAmount(
                          p.credits && p.credits.refundable
                            ? p.credits.refundable
                            : 0
                        )}
                      </td>
                      <td style={tdStyle}>
                        {formatCurrencyAmount(
                          p.credits && p.credits.nonRefundable
                            ? p.credits.nonRefundable
                            : 0
                        )}
                      </td>
                      <td style={tdStyle}>
                        {formatCurrencyAmount(p.initialTax)}
                      </td>
                      <td style={tdStyle}>
                        {formatCurrencyAmount(p.totalTax)}
                      </td>
                      <td style={tdStyle}>
                        {formatCurrencyAmount(p.totalTaxLiability)}
                      </td>
                      <td style={tdStyle}>
                        {p.effectiveRate ? p.effectiveRate + "%" : "N/A"}
                      </td>
                      <td style={tdStyle}>
                        {p.marginalRate ? p.marginalRate + "%" : "N/A"}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* Capital Gains Section */}

      {/*<div style={{ marginTop: "2rem", width: "50%" }}>
        <h3 className="text-lg font-semibold mb-2">Capital Gains Summary</h3>
        <div className="social-security-calultor-results">
          <div className="social-result-heading">Net Capital Gain:</div>
          <div className="social-result-value">
            {capitalGains.netCapitalGain &&
              formatCurrencyAmount(capitalGains.netCapitalGain)}
          </div>
        </div>
        <div className="social-security-calultor-results">
          <div className="social-result-heading">Capital Gains Tax:</div>
          <div className="social-result-value">
            {capitalGains.capitalGainsTax &&
              formatCurrencyAmount(capitalGains.capitalGainsTax)}
          </div>
        </div>
        <div className="social-security-calultor-results">
          <div className="social-result-heading">Total Tax Liability:</div>
          <div className="social-result-value">
            {capitalGains.totalTaxLiability &&
              formatCurrencyAmount(capitalGains.totalTaxLiability)}
          </div>
        </div>
      </div> */}
    </div>
  );
};

// Table Styles
const thStyle = {
  padding: "10px",
  fontFamily: "Poppins",
  borderBottom: "1px solid #ddd",
  fontWeight: "bold",
  color: "black",
  fontSize: "14px",
};

const tdStyle = {
  padding: "10px",
  fontSize: "14px",
  fontFamily: "Poppins",
  fontWeight: "500",
  color: "#6c757d",
  whiteSpace: "nowrap",
};
